import React, { useMemo, useState, useEffect, useCallback } from "react";
import "./AdminPanel.css";

import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useUserRole } from "../../hooks/useUserRole";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useFirestore } from "../../hooks/useFirestore";
import { useDocument } from "../../hooks/useDocument";
import NavCard from "../../components/NavCard";

import {
  AlarmClock,
  BarChart3,
  Bell,
  Brain,
  Calendar,
  CalendarDays,
  ClipboardList,
  FileText,
  FileUp,
  FolderOpen,
  Handshake,
  HelpCircle,
  Receipt,
  Search,
  Shield,
  Shirt,
  Timer,
  TrendingUp,
  Truck,
  Umbrella,
  Upload,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

export default function AdminPanel() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);
  const { isAdmin } = useUserRole();
  const { document: userDoc } = useDocument("users", user.uid);
  const { updateDocument } = useFirestore("users");

  const [searchTerm, setSearchTerm] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [openSections, setOpenSections] = useState({
    hr: true,
    presenze: true,
    finanza: true,
    operazioni: true,
    documenti: true,
    bi: true,
  });

  useEffect(() => {
    if (!userDoc) return;
    const fromDoc = Array.isArray(userDoc.adminFavorites)
      ? userDoc.adminFavorites
      : [];
    setFavorites(fromDoc.slice(0, 8));
  }, [userDoc]);

  const toggleFavorite = (action) => {
    setFavorites((prev) => {
      if (prev.includes(action)) {
        const updated = prev.filter((a) => a !== action);
        if (userDoc?.id) {
          updateDocument(userDoc.id, { adminFavorites: updated });
        }
        return updated;
      }
      if (prev.length >= 8) {
        return prev;
      }
      const updated = [...prev, action];
      if (userDoc?.id) {
        updateDocument(userDoc.id, { adminFavorites: updated });
      }
      return updated;
    });
  };

  const matchesSearch = useCallback(
    (elem) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const name = (elem.nome || "").toLowerCase();
      const msg = (elem.message || "").toLowerCase();
      return name.includes(term) || msg.includes(term);
    },
    [searchTerm],
  );

  const allElements = useMemo(
    () => [
      {
        action: "user-management",
        nome: "Gestione Profili Dipendenti",
        message: "Anagrafiche profili dei dipendenti",
        Icon: Users,
        tipo: "hr",
      },
      {
        action: "gestione-ruoli",
        nome: "Gestione Ruoli",
        message: "Gestisci i ruoli degli utenti del sistema",
        Icon: Shield,
        tipo: "hr",
      },
      {
        action: "vestiario",
        nome: "Gestione Vestiario",
        message: "Gestisci inventario e assegnazioni vestiario dipendenti",
        Icon: Shirt,
        tipo: "hr",
      },
      {
        action: "avvisi-dipendenti",
        nome: "Avvisi Dipendenti",
        message: "Gestisci gli avvisi per i dipendenti",
        Icon: Bell,
        tipo: "hr",
      },
      {
        action: "presenze-def",
        nome: "Gestione Presenze e Timbrature",
        message:
          "Visualizza e modifica i dati delle collezioni presenze e timbrature",
        Icon: Timer,
        tipo: "presenze",
      },
      {
        action: "presenze-admin",
        nome: "Pannello Amministrazione Presenze",
        message:
          "Gestisci presenze e Stampa i fogli di presenza dei dipendenti",
        Icon: CalendarDays,
        tipo: "presenze",
      },
      {
        action: "presenzeCheck",
        nome: "Verifica Presenze (Check)",
        message: "Sfoglia i fogli presenza dei dipendenti per mese",
        Icon: ClipboardList,
        tipo: "presenze",
      },
      {
        action: "supervisione-ferie-admin",
        nome: "Supervisione Ferie",
        message: "Gestisci e monitora le richieste ferie",
        Icon: Umbrella,
        tipo: "presenze",
      },
      {
        action: "timbratore",
        nome: "Timbratore",
        message: "Apri o chiudi una timbratura tramite QR code",
        Icon: Timer,
        tipo: "presenze",
      },
      {
        action: "tabella-paghe",
        nome: "Tabella Paghe",
        message: "Gestione stipendi e dati contrattuali",
        Icon: Wallet,
        tipo: "finanza",
      },
      {
        action: "speseCheck",
        nome: "Controlla Spese",
        message: "Verifica le spese di tutta l'azienda",
        Icon: Search,
        tipo: "finanza",
      },
      {
        action: "pianificazione-finanziaria",
        nome: "Pianificazione Finanziaria",
        message: "Gestisci saldo bancario e pianifica movimenti finanziari",
        Icon: TrendingUp,
        tipo: "finanza",
      },
      {
        action: "spaccatura-costi",
        nome: "Spaccatura Costi",
        message: "Gestione dettagliata della spaccatura costi",
        Icon: FileText,
        tipo: "finanza",
      },
      {
        action: "stampaSpese",
        nome: "Stampa Note Spese",
        message: "Stampa le note spese mensili dei dipendenti",
        Icon: Receipt,
        tipo: "finanza",
      },
      {
        action: "supervisione-dipendenti",
        nome: "Supervisione Attività Dipendenti",
        message: "Monitoraggio attività pianificate dei dipendenti",
        Icon: ClipboardList,
        tipo: "operazioni",
      },
      {
        action: "creazione-pos",
        nome: "Creazione POS",
        message: "Strumenti per elaborare un Piano Operativo di Sicurezza",
        Icon: FileText,
        tipo: "operazioni",
      },
      {
        action: "contratti-clienti",
        nome: "Contratti Clienti",
        message: "Gestione tariffe e contratti di manutenzione/reperibilità",
        Icon: Handshake,
        tipo: "operazioni",
      },
      {
        action: "automezzi-dashboard",
        nome: "Dashboard Automezzi",
        message: "Visualizza e aggiorna tutti i veicoli aziendali",
        Icon: Truck,
        tipo: "operazioni",
      },
      {
        action: "calendario-automezzi",
        nome: "Calendario Automezzi",
        message: "Visualizza l'utilizzo dei veicoli da parte degli utenti",
        Icon: Calendar,
        tipo: "hr",
      },
      {
        action: "document-scanner",
        nome: "Scanner AI Documenti",
        message: "Scansiona ed estrai dati dai documenti con Gemini AI",
        Icon: Brain,
        tipo: "documenti",
      },
      {
        action: "gestione-documenti",
        nome: "Gestione Documenti",
        message: "Visualizza e modifica i documenti scansionati",
        Icon: FileText,
        tipo: "documenti",
      },
      {
        action: "documenti-aziendali",
        nome: "Documenti Aziendali",
        message:
          "Archivio dei documenti societari con scadenze, download e aggiornamento",
        Icon: FolderOpen,
        tipo: "documenti",
      },
      {
        action: "registroAcquisti",
        nome: "Registro Acquisti",
        message: "Visualizza materiali acquistati e relativi dettagli",
        Icon: Receipt,
        tipo: "documenti",
      },
            {
        action: "consuntivi-list",
        nome: "Archivio Consuntivi",
        message: "Visualizza e gestisci i consuntivi salvati",
        Icon: FolderOpen,
        tipo: "documenti",
      },
      {
        action: "caricaDoc",
        nome: "Carica Documenti Dipendenti",
        message: "Metti a disposizione dei dipendenti i propri documenti",
        Icon: Upload,
        tipo: "hr",
      },
      {
        action: "richieste-info-documenti",
        nome: "Richiesta Info Documenti",
        message: "Crea ticket con fatture da classificare per settore",
        Icon: HelpCircle,
        tipo: "operazioni",
      },
      {
        action: "tickets",
        nome: "Tickets Interventi",
        message: "Registra e consulta tutte le richieste di intervento",
        Icon: ClipboardList,
        tipo: "operazioni",
      },
      {
        action: "analitica-admin",
        nome: "Analisi Dati Settoriali",
        message:
          "Visualizza e analizza i dati della collection analitica per settore",
        Icon: BarChart3,
        tipo: "bi",
      },
      {
        action: "scadenze-clienti",
        nome: "Scadenze Clienti",
        message: "Gestisci e monitora le scadenze dei clienti",
        Icon: AlarmClock,
        tipo: "bi",
      },
      {
        action: "csv-import",
        nome: "Importazione Progetti CSV",
        message: "Importa progetti da file CSV nel database",
        Icon: FileUp,
        tipo: "bi",
      },
      {
        action: "ricavi",
        nome: "Ricavi (Fatturazione)",
        message:
          "Gestisci le righe ricavo per la fatturazione (cliente, settore, descrizione, UM, importo)",
        Icon: Receipt,
        tipo: "bi",
      },
    ],
    [],
  );

  const baseMatches = useCallback(
    (elem) =>
      matchesSearch(elem) && (elem.action !== "gestione-ruoli" || isAdmin),
    [isAdmin, matchesSearch],
  );

  const favoriteElements = useMemo(
    () =>
      favorites
        .map((action) => allElements.find((e) => e.action === action))
        .filter(Boolean),
    [favorites, allElements],
  );

  const hrElements = useMemo(
    () => allElements.filter((e) => e.tipo === "hr" && baseMatches(e)),
    [allElements, baseMatches],
  );

  const presenzeElements = useMemo(
    () => allElements.filter((e) => e.tipo === "presenze" && baseMatches(e)),
    [allElements, baseMatches],
  );

  const finanzaElements = useMemo(
    () => allElements.filter((e) => e.tipo === "finanza" && baseMatches(e)),
    [allElements, baseMatches],
  );

  const operazioniElements = useMemo(
    () => allElements.filter((e) => e.tipo === "operazioni" && baseMatches(e)),
    [allElements, baseMatches],
  );

  const documentiElements = useMemo(
    () => allElements.filter((e) => e.tipo === "documenti" && baseMatches(e)),
    [allElements, baseMatches],
  );

  const biElements = useMemo(
    () => allElements.filter((e) => e.tipo === "bi" && baseMatches(e)),
    [allElements, baseMatches],
  );

  const sezioni = useMemo(
    () => [
      {
        id: "hr",
        titolo: "Gestione Personale (HR)",
        classe: "section-hr",
        Icon: Users,
        elementi: hrElements,
      },
      {
        id: "presenze",
        titolo: "Presenze e Orari",
        classe: "section-presenze",
        Icon: Timer,
        elementi: presenzeElements,
      },
      {
        id: "finanza",
        titolo: "Amministrazione e Finanza",
        classe: "section-finanza",
        Icon: Wallet,
        elementi: finanzaElements,
      },
      {
        id: "operazioni",
        titolo: "Operazioni e Asset",
        classe: "section-operazioni",
        Icon: Wrench,
        elementi: operazioniElements,
      },
      {
        id: "documenti",
        titolo: "Hub Documentale e AI",
        classe: "section-documenti",
        Icon: FolderOpen,
        elementi: documentiElements,
      },
      {
        id: "bi",
        titolo: "Business Intelligence e Dati",
        classe: "section-bi",
        Icon: BarChart3,
        elementi: biElements,
      },
    ],
    [
      biElements,
      documentiElements,
      finanzaElements,
      hrElements,
      operazioniElements,
      presenzeElements,
    ],
  );

  const overviewCards = useMemo(
    () =>
      sezioni.map((s) => ({
        id: `overview-${s.id}`,
        label: s.titolo,
        count: s.elementi.length,
        classe: `overview-${s.id}`,
        sectionId: s.id,
      })),
    [sezioni],
  );

  const handleScrollToSection = (sectionId) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: true }));
    const el = document.getElementById(`admin-section-${sectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  return (
    <div className="admin-panel-container">
      {admin && (
        <>
          <div className="admin-dashboard-header">
            <div className="admin-dashboard-title">
              <h1>Dashboard Amministrazione</h1>
              <p>
                Gestisci dipendenti, documenti, presenze, finanza e automezzi da
                un unico pannello
              </p>
            </div>
            <div className="admin-user-badge">
              <div className="admin-user-initials">
                {user?.displayName
                  ? user.displayName
                      .split(" ")
                      .map((part) => part[0])
                      .join("")
                      .toUpperCase()
                  : "AD"}
              </div>
              <div className="admin-user-info">
                <span className="admin-user-name">
                  {user?.displayName || user?.email || "Amministratore"}
                </span>
                <span className="admin-user-role">Admin</span>
              </div>
            </div>
          </div>

          {favoriteElements.length > 0 && (
            <div
              id="admin-section-preferiti"
              className="admin-section section-preferiti"
            >
              <div className="section-header-row">
                <h2 className="section-title">Preferiti</h2>
                <span className="section-counter">
                  {favoriteElements.length} di 8
                </span>
              </div>
              <div className="section-cards">
                {favoriteElements.map((elem) => (
                  <div key={elem.action} className="admin-favorite-card">
                    <NavCard
                      data={elem}
                      isFavorite={true}
                      onToggleFavorite={toggleFavorite}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="admin-overview-grid">
            {overviewCards.map((card) => (
              <div
                key={card.id}
                className={`overview-card ${card.classe}`}
                onClick={() => handleScrollToSection(card.sectionId)}
              >
                <span className="overview-label">{card.label}</span>
                <span className="overview-count">{card.count}</span>
              </div>
            ))}
          </div>

          <div className="admin-search-row">
            <input
              type="text"
              className="admin-search-input"
              placeholder="Cerca funzionalità per nome o descrizione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="admin-sections">
            {sezioni
              .filter((sezione) => sezione.elementi.length > 0)
              .map((sezione) => (
                <div
                  key={sezione.id}
                  id={`admin-section-${sezione.id}`}
                  className={`admin-section ${sezione.classe}`}
                >
                  <div
                    className="section-header-row admin-section-header"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSection(sezione.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSection(sezione.id);
                      }
                    }}
                  >
                    <h2 className="section-title">
                      <span className="admin-section-icon">
                        {sezione.Icon ? <sezione.Icon size={18} /> : null}
                      </span>
                      {sezione.titolo}
                    </h2>
                    <span className="section-counter">
                      {sezione.elementi.length} funzionalità
                      <span className="section-chevron">
                        {openSections[sezione.id] ? "▾" : "▸"}
                      </span>
                    </span>
                  </div>
                  {openSections[sezione.id] && (
                    <div className="section-cards">
                      {sezione.elementi.map((elem) => (
                        <div key={elem.nome} className="admin-favorite-card">
                          <NavCard
                            data={elem}
                            isFavorite={favorites.includes(elem.action)}
                            onToggleFavorite={toggleFavorite}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * - pulsante per inserire Task --> FATTO
 * - pulsante per inserire Spesa --> FATTO
 * - pulsante per andare al proprio profilo --> FATTO
 */
